import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, Play } from 'lucide-react';
import { UserAvatar } from '../../../components/ui/UserAvatar';
import { Button } from '../../../components/ui/Button';
import { useUserAvatarInfo } from '../../../hooks/useUserAvatarInfo';
import { useStudentMe } from '../../roster/hooks/useStudentMe';
import { useFeatureAccess } from '../../../contexts/FeatureAccessContext';
import { useTutorTenantLabels } from '../../tutor/hooks/useTutorTenantLabels';
import { useLearnerCurriculum } from '../../curriculum/hooks/useLearnerCurriculum';
import { isEffectiveMalayLearner } from '../../curriculum/learnerCurriculumOverride';
import { ROUTES } from '../../../utils/constants';
import { getTimeOfDayGreetingKey } from '../utils/greeting';
import { DashboardCardSkeleton } from './dashboardUi';

export function WelcomeHero() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSchoolFeatureEnabled } = useFeatureAccess();
  const { labels } = useTutorTenantLabels();
  const { curriculum } = useLearnerCurriculum();
  const isLearnerMalay = isEffectiveMalayLearner({
    curriculum,
    legacyTenantIsMalay: labels.isMalay,
  });
  const { avatarUrl, initials, displayName, isLoading: avatarLoading } = useUserAvatarInfo();
  const { student, grade, isRosterLoading } = useStudentMe();

  const name =
    displayName ||
    student?.display_name ||
    [student?.first_name, student?.last_name].filter(Boolean).join(' ') ||
    t('dashboard.studentHome.studentFallback');

  const greeting = t(getTimeOfDayGreetingKey());
  const loading = avatarLoading || isRosterLoading;

  const continueLearning = () => {
    const el = document.getElementById('continue-learning');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (isLearnerMalay) {
      if (isSchoolFeatureEnabled('ai_tutor')) {
        navigate(ROUTES.STUDENT_TAMIL_TUTOR);
        return;
      }
      if (isSchoolFeatureEnabled('narrative_writing')) {
        navigate(ROUTES.STUDENT_NARRATIVE_WRITING);
        return;
      }
    }
    if (isSchoolFeatureEnabled('vocabulary')) {
      navigate(ROUTES.STUDENT_VOCABULARY);
      return;
    }
    if (isSchoolFeatureEnabled('learning')) {
      navigate(ROUTES.STUDENT_ASSIGNMENTS);
      return;
    }
    navigate(ROUTES.STUDENT_TAMIL_TUTOR);
  };

  if (loading) {
    return <DashboardCardSkeleton variant="hero" />;
  }

  return (
    <section className="mb-6 overflow-hidden rounded-[1.75rem] border border-purple-200/80 bg-gradient-to-br from-indigo-50 via-white to-pink-50/60 shadow-xl shadow-purple-200/30 ring-1 ring-slate-200/80 dark:border-purple-500/30 dark:from-indigo-950/40 dark:via-gray-900 dark:to-purple-950/30 dark:shadow-purple-950/20 dark:ring-white/5">
      <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-7">
        <div className="flex items-center gap-4 min-w-0">
          <div className="shrink-0 ring-4 ring-white/80 dark:ring-purple-500/20 rounded-full shadow-lg">
            <UserAvatar
              avatarUrl={avatarUrl}
              initials={initials}
              size="profile"
              className="h-16 w-16 !bg-white dark:!bg-gray-800"
            />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-purple-700 dark:text-purple-300">{greeting}</p>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white truncate">
              {name}
            </h1>
            {grade != null ? (
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                {t('dashboard.studentHome.gradeLabel', { grade })}
              </p>
            ) : null}
          </div>
        </div>
        <Button
          type="button"
          onClick={continueLearning}
          className="shrink-0 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-purple-500/25 hover:brightness-110 min-h-[44px]"
        >
          <Play className="h-4 w-4 fill-current" aria-hidden />
          {t('dashboard.studentHome.continueLearning')}
        </Button>
      </div>
      <div className="hidden sm:flex items-center gap-2 border-t border-purple-100/80 bg-white/40 px-7 py-3 text-xs text-slate-600 dark:border-purple-500/20 dark:bg-white/[0.03] dark:text-slate-400">
        <BookOpen className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" aria-hidden />
        <span>{t('dashboard.studentHome.heroSubtitle')}</span>
      </div>
    </section>
  );
}
